import { SearchCategories } from 'src/app/components/search/search.enum';
import { MapState } from 'src/app/interfaces/map.interface';
import { Coords, FullUser, SearchResult, SearchResultAggregation } from 'src/app/swagger-api';
import { SearchAggregationResponse } from 'src/app/swagger-api/model/searchAggregationResponse';
import { AggregationKey } from '../../map.interfaces';
import MapSettings from '../../map.settings';
import { MapValues } from '../../map.values';
import { AggregationResponseCoordsProcessorDecorator } from './aggregation-response-coords-processor.decorator';
import { AggregationResponseProcessorDecorator } from './aggregation-response-processor.decorator';
import {
  AggregationProcessorType,
  NonAggregationProcessorType,
  SharedResponseProcessor,
} from './shared-response-processor.decorator';
import { UserCoordsProcessorDecorator } from './user-coords-processor.decorator';
import { UserResponseProcessorDecorator } from './user-response-processor.decorator';
import { OrganizationAggregationResponseProcessorDecorator } from './organization-aggregation-response-processor.decorator';

export function isUserEntityKind(result: SearchResult): boolean {
  return result.entity_kind === MapSettings.USER_ENTITY_KIND;
}

export function getSearchAggregationDecoratedResponse(
  response: SearchAggregationResponse,
  state: MapState,
): SearchAggregationResponse {
  const list: AggregationProcessorType = new SharedResponseProcessor(response);
  let decoratedList: AggregationProcessorType = list;

  if (
    response.length &&
    response[0].aggregated_entities.length &&
    (response[0].aggregated_entities[0] === SearchCategories.INDIVIDUALS ||
      MapValues.mapStatisticsSearchIndividualRolesKeys.some(
        (role: string) => role === response[0].aggregated_entities[0],
      ))
  ) {
    decoratedList = new AggregationResponseProcessorDecorator(list, state);
  }

  if (
    response.length &&
    response[0].aggregated_entities.length &&
    response[0].aggregated_entities[0] === SearchCategories.ORGANIZATIONS
  ) {
    decoratedList = new OrganizationAggregationResponseProcessorDecorator(list, state);
  }

  decoratedList = new AggregationResponseCoordsProcessorDecorator(decoratedList, state);
  return decoratedList.getProcessedItems();
}

export function getSearchNonAggregationDecoratedResponse(results: SearchResult[], state: MapState): SearchResult[] {
  const list: NonAggregationProcessorType = new SharedResponseProcessor(results);
  const userDecoratedList: NonAggregationProcessorType = new UserResponseProcessorDecorator(list, state);

  if (results.length && !isUserEntityKind(results[0])) {
    return userDecoratedList.getProcessedItems();
  }

  const coordsDecoratedList: NonAggregationProcessorType = new UserCoordsProcessorDecorator(userDecoratedList, state);
  return coordsDecoratedList.getProcessedItems();
}

export function getSearchAggregationDecoratedResult(
  item: SearchResultAggregation,
  index: number,
  coords: Coords,
  // TODO will be changed after reGenerate Swagger
): SearchResultAggregation {
  const aggregationKey: AggregationKey = item.buckets[0].aggregation_key as AggregationKey;
  // note: All markers has same location_literal and tooltips, they have one parent bucket.
  return {
    ...item,
    buckets: [
      {
        aggregated_items_count: item.buckets[index].aggregated_items_count,
        aggregation_key: {
          center_coordinates: coords,
          location_literal: aggregationKey.location_literal,
          tooltips: aggregationKey.tooltips,
        },
      },
    ],
  };
}

export function getSearchNonAggregationUserDecoratedResult(result: SearchResult, center?: Coords): SearchResult {
  const fullUser: FullUser = result.item as FullUser;
  return {
    ...result,
    item: {
      ...result.item,
      profile: {
        ...fullUser.profile,
        location: { ...fullUser.profile.location, center },
      },
    },
  };
}

/**
 * @description A simple implementation of reduce along with the usage examples with type
 *              arguments provided explicitly.
 * @param { TElement[] } array
 * @param { (result: TResult, el: TElement) => TResult } reducer
 * @param { TResult } initialResult
 * @returns { TResult }
 */
export function internalReduce<TElement, TResult>(
  array: TElement[],
  reducer: (result: TResult, el: TElement) => TResult,
  initialResult: TResult,
): TResult {
  let result: TResult = initialResult;
  for (const element of array) {
    result = reducer(result, element);
  }
  return result;
}
